// components/GameCard.tsx
import React, { useMemo } from 'react';
import {
  IonCard,
  IonCardContent,
  IonChip,
  IonIcon,
  IonButton,
} from '@ionic/react';
import { create, trash, star, cube, compass, gameController } from 'ionicons/icons';
import { UserGame } from '../types/games';
import './GameCard.css';

interface GameCardProps {
  game: UserGame;
  onEdit: (game: UserGame) => void;
  onDelete: (gameId: string) => void;
  showActions?: boolean;
}

export const GameCard = React.memo<GameCardProps>(
  ({ game, onEdit, onDelete, showActions = true }) => {
    // Memoize ownership label calculation
    const ownershipInfo = useMemo(() => {
      switch (game.ownership_status) {
        case 'own':
          return { text: 'I Own This', icon: cube, color: 'success' };
        case 'want-to-try':
          return { text: 'Want to Try', icon: compass, color: 'warning' };
        case 'played':
          return { text: 'Played', icon: star, color: 'medium' };
        default:
          return null;
      }
    }, [game.ownership_status]);

    // Memoize TTRPG label calculation
    const ttrpgInfo = useMemo(() => {
      switch (game.ttrpg_preference) {
        case 'play':
          return { text: 'Player', icon: gameController, color: 'secondary' };
        case 'run':
          return { text: 'GM/DM', icon: gameController, color: 'tertiary' };
        case 'both':
          return { text: 'Player & GM', icon: gameController, color: 'primary' };
        default:
          return null;
      }
    }, [game.ttrpg_preference]);

    // Memoize RPG detection
    const isRPG = useMemo(() => {
      return (
        game.game_type?.toLowerCase().includes('rpg') ||
        game.name?.toLowerCase().includes('rpg') ||
        game.name?.toLowerCase().includes('dungeons')
      );
    }, [game.game_type, game.name]);

    return (
      <IonCard className="game-card">
        <div className="game-card-content">
          <IonCardContent className="game-details">
            <div className="game-header">
              <h3 className="game-name">{game.name}</h3>
              {game.year_published && (
                <span className="game-year">({game.year_published})</span>
              )}
            </div>

            <div className="game-meta">
              {game.min_players && game.max_players && (
                <span className="meta-item">
                  👥 {game.min_players}-{game.max_players} players
                </span>
              )}
              {game.min_playtime && game.max_playtime && (
                <span className="meta-item">
                  ⏱️ {game.min_playtime}-{game.max_playtime} min
                </span>
              )}
            </div>

            <div className="game-tags">
              {ownershipInfo && (
                <IonChip color={ownershipInfo.color} className="ownership-chip">
                  <IonIcon icon={ownershipInfo.icon} />
                  {ownershipInfo.text}
                </IonChip>
              )}

              {isRPG && ttrpgInfo && (
                <IonChip color={ttrpgInfo.color} className="ttrpg-chip">
                  <IonIcon icon={ttrpgInfo.icon} />
                  {ttrpgInfo.text}
                </IonChip>
              )}
            </div>

            {showActions && (
              <div className="game-actions">
                <IonButton
                  size="small"
                  fill="clear"
                  onClick={() => onEdit(game)}
                >
                  <IonIcon icon={create} slot="icon-only" />
                </IonButton>
                <IonButton
                  size="small"
                  fill="clear"
                  color="danger"
                  onClick={() => onDelete(game.id)}
                >
                  <IonIcon icon={trash} slot="icon-only" />
                </IonButton>
              </div>
            )}
          </IonCardContent>
        </div>
      </IonCard>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for optimal re-render prevention
    return (
      prevProps.game.id === nextProps.game.id &&
      prevProps.game.name === nextProps.game.name &&
      prevProps.game.ownership_status === nextProps.game.ownership_status &&
      prevProps.game.ttrpg_preference === nextProps.game.ttrpg_preference &&
      prevProps.game.year_published === nextProps.game.year_published &&
      prevProps.game.min_players === nextProps.game.min_players &&
      prevProps.game.max_players === nextProps.game.max_players &&
      prevProps.game.min_playtime === nextProps.game.min_playtime &&
      prevProps.game.max_playtime === nextProps.game.max_playtime &&
      prevProps.game.game_type === nextProps.game.game_type &&
      prevProps.showActions === nextProps.showActions &&
      prevProps.onEdit === nextProps.onEdit &&
      prevProps.onDelete === nextProps.onDelete
    );
  }
);

GameCard.displayName = 'GameCard';